import { useParams, Link } from "react-router-dom";
import { useState, useMemo } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import SEO from "@/components/base/SEO";
import { siteCategories } from "@/mocks/categories";
import { getVideosByCategory } from "@/mocks/videos";
import type { BreadcrumbItem } from "@/components/base/Breadcrumb";
import type { VideoItem } from "@/mocks/videos";

function VideoCard({ video }: { video: VideoItem }) {
  return (
    <Link
      to={`/video/${video.id}`}
      className="group block cursor-pointer"
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
    >
      <div className="relative overflow-hidden rounded-lg mb-3 aspect-[3/4]">
        <img
          src={video.image}
          alt={video.title}
          title={`${video.title} - 桃子视频高清在线观看`}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        <div className="absolute top-2 left-2 flex gap-1.5">
          {video.isHot && (
            <span className="px-2 py-0.5 text-[10px] font-semibold bg-accent-500 text-background-50 rounded-full whitespace-nowrap">
              热门
            </span>
          )}
          {video.isNew && (
            <span className="px-2 py-0.5 text-[10px] font-semibold bg-secondary-500 text-background-50 rounded-full whitespace-nowrap">
              新上
            </span>
          )}
        </div>
        <div className="absolute top-2 right-2">
          <span className="px-2 py-0.5 text-[10px] font-semibold bg-black/60 text-white rounded-full">
            {video.rating}
          </span>
        </div>
      </div>
      <h3 className="text-sm font-semibold text-foreground-950 line-clamp-1 group-hover:text-accent-500 transition-colors">
        {video.title}
      </h3>
      <div className="flex items-center gap-2 mt-1 text-xs text-foreground-500">
        <span>{video.brand}</span>
        <span>·</span>
        <span>{video.year}</span>
      </div>
    </Link>
  );
}

function CategoryJsonLd({ categoryName, categorySlug }: { categoryName: string; categorySlug: string }) {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${categoryName} - 桃子视频`,
    description: `桃子视频${categoryName}分类，免费高清在线观看最新${categoryName}资源，更新快不缓冲。`,
    url: `https://www.taozivideo.com/category/${categorySlug}`,
    isPartOf: {
      "@type": "WebSite",
      name: "桃子视频",
      url: "https://www.taozivideo.com",
    },
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://www.taozivideo.com" },
        { "@type": "ListItem", position: 2, name: categoryName },
      ],
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
    />
  );
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentSlug = slug || "all";

  const [sortBy, setSortBy] = useState<"hot" | "new" | "rating">("hot");

  const currentCategory = useMemo(
    () => siteCategories.find((c) => c.slug === currentSlug),
    [currentSlug]
  );

  const categoryName = currentCategory?.name || "全部";

  const allVideos = useMemo(() => getVideosByCategory(currentSlug), [currentSlug]);

  const sortedVideos = useMemo(() => {
    const list = [...allVideos];
    if (sortBy === "hot") return list.sort((a, b) => b.views - a.views);
    if (sortBy === "new") return list.sort((a, b) => b.year - a.year);
    return list.sort((a, b) => b.rating - a.rating);
  }, [allVideos, sortBy]);

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: "首页", href: "/" },
    { label: categoryName },
  ];

  return (
    <>
      <SEO
        title={`${categoryName} - 桃子视频免费高清在线观看 | 蜜桃樱桃芒果蓝莓视频`}
        description={`桃子视频${categoryName}分类，免费高清在线观看最新${categoryName}资源，电影电视剧综艺全集不缓冲，更新快画质清晰。`}
        keywords={`${categoryName},桃子视频,高清影视,在线观看,全集`}
        canonicalUrl={`https://www.taozivideo.com/category/${currentSlug}`}
      />
      <CategoryJsonLd categoryName={categoryName} categorySlug={currentSlug} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="w-full px-4 md:px-6 py-6">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />

              {/* Category Header */}
              <div className="mb-8">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                  {categoryName}
                </h1>
                <p className="text-sm text-foreground-500">
                  共 {allVideos.length} 部影视作品
                </p>
              </div>

              {/* Category Tabs */}
              <div className="mb-8 overflow-x-auto">
                <div className="flex items-center gap-2 min-w-max">
                  {siteCategories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.slug}`}
                      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                        currentSlug === cat.slug
                          ? "bg-accent-500 text-background-50"
                          : "bg-background-100 text-foreground-600 hover:bg-background-200"
                      }`}
                    >
                      <i className={`${cat.icon} text-sm`}></i>
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Sort Options */}
              <div className="flex items-center gap-2 mb-6">
                <span className="text-sm text-foreground-500 mr-2 whitespace-nowrap">排序：</span>
                {[
                  { key: "hot", label: "按热度" },
                  { key: "new", label: "按时间" },
                  { key: "rating", label: "按评分" },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setSortBy(opt.key as typeof sortBy)}
                    className={`px-4 py-1.5 text-sm rounded-full transition-all whitespace-nowrap cursor-pointer ${
                      sortBy === opt.key
                        ? "bg-foreground-950 text-background-50"
                        : "bg-background-100 text-foreground-600 hover:bg-background-200"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {/* Video Grid */}
              {sortedVideos.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-product-shop>
                  {sortedVideos.map((video) => (
                    <VideoCard key={video.id} video={video} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                    <i className="ri-movie-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无内容，请查看其他分类</p>
                </div>
              )}
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}